import { theme } from '../../lib/theme';
import {
  Users, FileText, Calendar, Home, BarChart3, Sparkles,
  Mail, DollarSign, ClipboardList, Target, Plus,
} from 'lucide-react';

/**
 * EmptyState — actionable empty state for any module.
 *
 * Usage:
 *   <EmptyState
 *     type="contacts"
 *     onAction={() => setShowAddContact(true)}
 *   />
 *
 *   <EmptyState
 *     icon={<CustomIcon />}
 *     title="No results"
 *     description="Try adjusting your filters."
 *   />
 */

const PRESETS = {
  contacts: {
    icon: Users,
    title: 'No contacts yet',
    description: 'Add your first contact to start building your CRM. Import from a spreadsheet or add one manually.',
    actionLabel: 'Add Contact',
    secondaryLabel: 'Import CSV',
  },
  leads: {
    icon: Users,
    title: 'No leads in your pipeline',
    description: 'Leads from Zillow, Facebook, and your website will appear here. You can also add leads manually.',
    actionLabel: 'Add Lead',
    secondaryLabel: 'Connect Lead Sources',
  },
  deals: {
    icon: FileText,
    title: 'No active deals',
    description: 'Create your first deal to start tracking milestones, documents, and closing timelines.',
    actionLabel: 'Create Deal',
  },
  tasks: {
    icon: Target,
    title: 'All caught up!',
    description: 'No tasks right now. Tasks are auto-created from deal milestones and action plans.',
    actionLabel: 'Create Task',
  },
  showings: {
    icon: Home,
    title: 'No showings scheduled',
    description: 'Schedule showings for your listings or set up booking links for clients to self-schedule.',
    actionLabel: 'Schedule Showing',
    secondaryLabel: 'Create Booking Link',
  },
  calendar: {
    icon: Calendar,
    title: 'Calendar is empty',
    description: 'Connect your Google or Outlook calendar to see your schedule here.',
    actionLabel: 'Connect Calendar',
  },
  reports: {
    icon: BarChart3,
    title: 'Not enough data yet',
    description: 'Reports will populate as you add deals, leads, and close transactions. Check back soon!',
  },
  documents: {
    icon: FileText,
    title: 'No documents',
    description: 'Upload contracts, disclosures, and other deal documents. They\'ll be organized by deal automatically.',
    actionLabel: 'Upload Document',
  },
  messages: {
    icon: Mail,
    title: 'No messages yet',
    description: 'Messages from clients and your deal room conversations will appear here.',
  },
  commissions: {
    icon: DollarSign,
    title: 'No commissions tracked',
    description: 'Commission data appears automatically when deals close. Your earnings history will build over time.',
  },
  listings: {
    icon: ClipboardList,
    title: 'No listings',
    description: 'Create a listing once and Trovva formats it for every platform — MLS, Zillow, social media.',
    actionLabel: 'Create Listing',
  },
  studio: {
    icon: Sparkles,
    title: 'Welcome to Studio',
    description: 'Generate listing descriptions, social posts, emails, and more with AI. Pick a template to get started.',
    actionLabel: 'Browse Templates',
  },
  search: {
    icon: Users,
    title: 'No results found',
    description: 'Try adjusting your search terms or filters to find what you\'re looking for.',
  },
};

export default function EmptyState({
  type,
  icon: customIcon,
  title: customTitle,
  description: customDesc,
  actionLabel: customAction,
  secondaryLabel: customSecondary,
  onAction,
  onSecondaryAction,
  compact = false,
}) {
  const preset = PRESETS[type] || {};
  const IconComponent = preset.icon || Users;
  const title = customTitle || preset.title || 'Nothing here yet';
  const description = customDesc || preset.description || '';
  const actionLabel = customAction || preset.actionLabel;
  const secondaryLabel = customSecondary || preset.secondaryLabel;

  return (
    <div style={{
      textAlign: 'center',
      padding: compact ? '24px 16px' : '48px 24px',
      maxWidth: 420,
      margin: '0 auto',
    }}>
      {/* Icon */}
      <div style={{
        width: compact ? 48 : 64,
        height: compact ? 48 : 64,
        borderRadius: compact ? 14 : 18,
        background: `${theme.colors.primary}10`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 16px',
      }}>
        {customIcon || <IconComponent size={compact ? 24 : 30} color={theme.colors.primary} />}
      </div>

      {/* Title */}
      <h3 style={{
        color: theme.colors.text,
        fontSize: compact ? 15 : 18,
        fontWeight: 700,
        margin: '0 0 8px',
      }}>
        {title}
      </h3>

      {/* Description */}
      <p style={{
        color: theme.colors.textLight,
        fontSize: compact ? 13 : 14,
        lineHeight: 1.5,
        margin: '0 0 20px',
      }}>
        {description}
      </p>

      {/* Actions */}
      {(actionLabel || secondaryLabel) && (
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          {actionLabel && onAction && (
            <button
              onClick={onAction}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '10px 20px',
                borderRadius: theme.radius.sm,
                background: theme.colors.primary,
                color: 'white',
                border: 'none',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <Plus size={16} />
              {actionLabel}
            </button>
          )}
          {secondaryLabel && onSecondaryAction && (
            <button
              onClick={onSecondaryAction}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '10px 20px',
                borderRadius: theme.radius.sm,
                background: 'transparent',
                color: theme.colors.primary,
                border: `1.5px solid ${theme.colors.primary}`,
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {secondaryLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
